import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Plus, Loader2, ShoppingBag, ChevronRight, AlertTriangle } from 'lucide-react';
import { PoStatusBadge, pence, ageLabel } from '../../components/portal/orders/poUtils';
import { jobCode, isConstruction } from '../../utils/jobCode';

const FILTERS = [['open', 'Open'], ['collected', 'Collected'], ['all', 'All']];

export function PortalOrders() {
  const { api, features } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('open');

  const load = useCallback(async () => {
    try { const r = await api.get('/portal/orders'); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load orders'); setData({ items: [] }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const items = (data?.items || []).filter(o => filter === 'all' || o.status === filter);
  if (!features?.purchase_orders) {
    return (
      <div className="p-4" data-testid="orders-page">
        <div className="py-12 text-center text-gray-400 space-y-3" data-testid="orders-off">
          <ShoppingBag className="w-10 h-10 mx-auto opacity-40" />
          <p className="text-white font-semibold">Purchase orders are switched off</p>
          <p className="text-sm">An admin can turn them on under Settings → Features.</p>
          <Button variant="outline" onClick={() => navigate('/portal')} className="h-11 border-white/20 text-white bg-white/5" data-testid="orders-off-home">Back to Home</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-4 space-y-4" data-testid="orders-page">
      <Button onClick={() => navigate('/portal/orders/new')} disabled={data?.blocking} className="w-full h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold text-base" data-testid="new-order-btn"><Plus className="w-5 h-5 mr-2" /> Get an order number</Button>
      {data?.blocking && <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 flex gap-2" data-testid="orders-blocking"><AlertTriangle className="w-4 h-4 shrink-0" /> An order has been open for over 7 days — collect it or cancel it before raising another.</div>}
      {!data?.blocking && data?.overdue > 0 && <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="orders-overdue">{data.overdue} order{data.overdue === 1 ? '' : 's'} open for more than 48 hours — record what was collected or cancel.</div>}
      <div className="flex gap-2">
        {FILTERS.map(([k, l]) => <button key={k} type="button" onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs border ${filter === k ? 'bg-white text-[#1E2530] border-white font-semibold' : 'bg-white/5 border-white/10 text-gray-300'}`} data-testid={`orders-filter-${k}`}>{l} <span className="opacity-60">({(data?.items || []).filter(o => k === 'all' || o.status === k).length})</span></button>)}
      </div>
      {data === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div> : items.length === 0 ? (
        <div className="py-12 text-center text-gray-500" data-testid="orders-empty"><ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-40" />No {filter === 'all' ? '' : filter} orders.</div>
      ) : (
        <div className="space-y-2" data-testid="orders-list">
          {items.map(o => (
            <button key={o.id} type="button" onClick={() => navigate(`/portal/orders/${o.id}`)} className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 active:scale-[0.99] ${o.status === 'open' && o.overdue ? 'border-red-500/40 bg-red-500/5' : 'border-white/10 bg-[#2A3240]'}`} data-testid={`order-row-${o.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="text-white font-bold tabular-nums" data-testid={`order-number-${o.id}`}>{o.number}</span><PoStatusBadge status={o.status} overdue={o.overdue} /></div>
                <div className="text-xs text-gray-400 truncate"><span className={isConstruction(o) ? 'text-blue-400' : 'text-[#F5A623]'}>{jobCode(o, features)}</span> · {o.supplier_name}{o.supplier_account ? <span className="font-mono text-gray-300"> · {o.supplier_account}</span> : ''}</div>
                <div className="text-[11px] text-gray-500">{format(new Date(o.raised_at), 'd MMM HH:mm')}{o.status === 'open' ? ` · ${ageLabel(o.age_hours)}` : ''}</div>
              </div>
              {o.amount_pence != null && <div className="text-white font-semibold tabular-nums">{pence(o.amount_pence)}</div>}
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
